import { ChangeDetectorRef, Component, computed, input, OnInit, output } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { QuestionOption } from '@empathy/survey/base';
import { Question } from '@empathy/survey/base';

@Component({
  selector: 'empathy-multi-choice-question-view',
  templateUrl: 'multi-choice-question-view.component.html',
  styleUrls: ['./multi-choice-question-view.component.scss', './_multi-choice-question-view-theme.component.scss']
})
export class MultiChoiceQuestionViewComponent implements OnInit {
  question = input.required<Question>();
  questionGrp = input.required<FormGroup>();

  forceStop = output<boolean>();

  visibleOptions$ = computed(() => this.question().visibleOptions);
  useMedia$ = computed(() => this.question().visibleOptions.some((option) => !!option.stimulus?.mediaId));

  otherOption: Partial<QuestionOption> = {
    value: 'OTHER',
    text: 'system.other'
  };

  constructor(private cdr: ChangeDetectorRef) {}

  private get selectedValues(): string[] {
    return this.questionGrp().get('value')?.value || [];
  }

  private get exclusiveOptionValues(): string[] {
    const configuredExclusiveOptions = this.question()
      .options.filter((option) => option.exclusiveInd)
      .map((option) => option.value);

    if (this.question().useExclusiveOtherInd) {
      return [...configuredExclusiveOptions, this.otherOption.value];
    }

    return configuredExclusiveOptions;
  }

  ngOnInit(): void {
    this.otherOption.exclusiveInd = this.question().useExclusiveOtherInd;
    this.questionGrp()
      .get('otherText')
      ?.valueChanges.subscribe((value) => {
        this.forceStop.emit(value === null);
      });
  }

  onMediaChecked(option: Partial<QuestionOption>): void {
    const index = this.selectedValues.findIndex((opt) => opt === option.value);

    this.onChecked({ checked: index === -1 }, option);
  }

  onChecked(event: { checked: boolean }, option: Partial<QuestionOption>): void {
    let currentValues = this.selectedValues;

    if (this.question().maxLength > 0 && event.checked && currentValues.length === this.question().maxLength) {
      return;
    }

    if (event.checked) {
      if (this.question().maxLength > 0 && this.questionGrp().get('value')?.value?.length === this.question().maxLength) {
        return;
      }

      this.clearExclusiveOptions(currentValues);
      if (option.exclusiveInd) {
        currentValues = [];
      }
      this.questionGrp()
        .get('value')
        ?.setValue([...currentValues, option.value]);
    } else {
      const uncheckedOptionIndex = currentValues.findIndex((opt) => opt === option.value);
      currentValues.splice(uncheckedOptionIndex, 1);
      this.questionGrp()
        .get('value')
        ?.setValue([...currentValues]);
    }

    this.validateQuestion(option, event.checked);

    this.cdr.detectChanges();
  }

  private clearExclusiveOptions(currentValue: string[]): void {
    this.exclusiveOptionValues.forEach((value) => {
      const index = currentValue.findIndex((opt) => opt === value);
      if (index > -1) {
        currentValue.splice(index, 1);
      }
    });
  }

  private validateQuestion(option: Partial<QuestionOption>, checked: boolean): void {
    if (option.value === this.otherOption.value) {
      this.forceStop.emit(checked && !this.questionGrp().get('otherText')?.value);
      return;
    }

    if ((option.exclusiveInd && checked) || this.selectedValues.length >= this.question().minLength) {
      this.forceStop.emit(false);
    } else {
      this.forceStop.emit(true);
    }
  }
}
